import {type CSSProperties, Fragment, type ReactNode, useEffect, useMemo, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import {autoUpdate, type ReferenceElement} from '@floating-ui/dom';
import {EmbeddedBlock, EmbeddedBlockVariant} from '@tinct/core-api-models';
import {BlockOverlay} from '../components/block-overlay';
import {BlockBadges} from '../components/block-badges';
import {isBlockPersonalizationEnabled} from '../../../../../utils/is-block-personalization-enabled';
import {OwnedOverlayPortal} from '../components/owned-overlay-portal';
import {useOverlayHost} from './overlay-host-context';
import {getCandidatesForWidth} from './css-selector-utils';

// Shared identity: OwnedOverlayPortal re-applies containerStyle whenever the
// object changes, so an inline literal would write to the iframe CSSOM once
// per block on every render.
const DISPLAY_CONTENTS: CSSProperties = { display: 'contents' };

function findBlockElement(
  block: EmbeddedBlock,
  doc: Document,
): Element | null {
  if (!block.cssSelectors) return null;

  const width = doc.defaultView?.innerWidth ?? 1280;
  const candidates = getCandidatesForWidth(block.cssSelectors, width);

  for (const { selectors } of candidates) {
    for (const selector of selectors) {
      try {
        const element = doc.querySelector(selector);
        if (element && !element.closest('[data-tinct-overlay]')) return element;
      } catch {
        // Skip invalid selectors
      }
    }
  }

  return null;
}

/**
 * Inline-level elements (`display: inline`: an <em>, <span>, <a> in a line of
 * text) can't host the inset ring: their box is the line fragments, so an
 * `absolute; inset: 0` child covers first-to-last fragment only. Promoting
 * them to `inline-block` (the previous approach) changes their line wrapping
 * — on a page that stacks two copies of a headline for a visual effect, the
 * promoted copy wrapped differently from its twin and both showed through.
 * These targets get a ring that follows their bounding box instead
 * (InlineRingPortal); nothing about their layout is touched.
 */
function isInlineLevel(el: Element): boolean {
  return getComputedStyle(el).display === 'inline';
}

function ensurePositioned(el: HTMLElement) {
  // The overlay is an absolutely-positioned child with `inset: 0`, so it fills
  // the element's box only if the element is positioned.
  //
  // The write is guarded on the inline style being unset first: without it,
  // page CSS using `... !important` keeps `getComputedStyle` reporting the
  // original value no matter what we write, so every render would rewrite the
  // inline style — firing a mutation the DOM-revision observer picks up and
  // re-rendering forever.
  if (!el.style.position && getComputedStyle(el).position === 'static') {
    el.style.position = 'relative';
  }
}

const FREEZE_TRANSFORM_ATTR = 'data-tinct-freeze-transform';
const FREEZE_TRANSFORM_VAR = '--tinct-frozen-transform';
const FREEZE_TRANSFORM_STYLE_ID = 'tinct-freeze-transform-style';

/**
 * Pins the `transform` of a target where it currently is, so it stops moving —
 * used for `customer_logo` blocks, whose Swiper/marquee track auto-slides
 * (`translate3d(...)`) and drags the overlay out of the visible viewport.
 *
 * Pinned at the CURRENT offset, never reset to `none`: a marquee's items are
 * laid out for its running offset, so with no transform they can all sit
 * outside the viewport and the strip looks empty (seen on an 800px artboard:
 * first logo at x≈2600). Freezing where it is keeps the logos that were
 * visible exactly where they were.
 *
 * Swiper rewrites the track's inline `transform` every animation frame, so an
 * inline override would be wiped by the next write. Instead we tag the element
 * and pin it with a stylesheet `!important` rule reading a custom property
 * the per-frame writes never touch. The <style> goes in <head> (outside the
 * body subtree the DOM-revision observer watches) and the attribute isn't a
 * `class`, so neither write triggers a re-render loop.
 */
function freezeTransform(el: HTMLElement, doc: Document): { translated: boolean } {
  // Idempotent: the targets memo re-runs on every DOM revision; re-pinning
  // would make the frozen track jump to wherever it would have been.
  if (!el.hasAttribute(FREEZE_TRANSFORM_ATTR)) {
    const current = doc.defaultView?.getComputedStyle(el).transform;
    el.style.setProperty(FREEZE_TRANSFORM_VAR, current && current !== 'none' ? current : 'none');
    el.setAttribute(FREEZE_TRANSFORM_ATTR, '');
  }

  if (!doc.getElementById(FREEZE_TRANSFORM_STYLE_ID)) {
    const style = doc.createElement('style');
    style.id = FREEZE_TRANSFORM_STYLE_ID;
    style.textContent = `[${FREEZE_TRANSFORM_ATTR}]{transform:var(${FREEZE_TRANSFORM_VAR}, none) !important;}`;
    (doc.head ?? doc.documentElement).appendChild(style);
  }

  return { translated: el.style.getPropertyValue(FREEZE_TRANSFORM_VAR) !== 'none' };
}

/** Lets a frozen track move again (the element is no longer an overlay target). */
function unfreezeTransform(el: HTMLElement) {
  if (!el.hasAttribute(FREEZE_TRANSFORM_ATTR)) return;
  el.removeAttribute(FREEZE_TRANSFORM_ATTR);
  el.style.removeProperty(FREEZE_TRANSFORM_VAR);
}

/**
 * Watches the iframe document body for DOM mutations (ignoring overlay nodes)
 * and returns a revision counter that increments on each relevant change.
 * Also watches for iframe resize to re-resolve screen-size-dependent selectors.
 */
function useIframeDomRevision(contentDocument: Document | null): number {
  const [revision, setRevision] = useState(0);

  useEffect(() => {
    if (!contentDocument?.body) return;

    // Throttle bumps with a 500ms timer so a mutation burst (animations,
    // layout thrash, cascading React updates) collapses to a single
    // re-render. The first mutation arms the timer; further mutations within
    // the window are ignored, so we fire at most once per 500ms regardless of
    // how busy the stream is.
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    const bump = () => {
      if (timeoutId != null) return;
      timeoutId = setTimeout(() => {
        timeoutId = null;
        setRevision((r) => r + 1);
      }, 500);
    };

    const isOverlayNode = (n: Node): boolean =>
      n instanceof Element &&
      (n.hasAttribute('data-tinct-overlay') ||
        !!n.closest('[data-tinct-overlay]'));

    const mutationObserver = new MutationObserver((mutations) => {
      const hasRelevantMutation = mutations.some((m) => {
        // Anything happening inside (or on) an overlay subtree is ours.
        const targetEl =
          m.target.nodeType === Node.ELEMENT_NODE
            ? (m.target as Element)
            : m.target.parentElement;
        if (targetEl?.closest('[data-tinct-overlay]')) return false;

        if (m.type === 'childList') {
          const touched = [...m.addedNodes, ...m.removedNodes];
          if (touched.length === 0) return false;
          // Require at least one non-overlay Element to consider this
          // relevant. Text/comment nodes don't affect CSS-selector matching,
          // and pure overlay add/remove is our own work.
          const hasNonOverlayElement = touched.some(
            (n) => n instanceof Element && !isOverlayNode(n),
          );
          if (!hasNonOverlayElement) return false;
        }

        return true;
      });

      if (hasRelevantMutation) bump();
    });

    // `class` mutations can change which selectors match; `style` is too
    // noisy (Framer Motion / JS animations rewrite inline style every frame
    // on non-overlay elements). Floating UI's `autoUpdate` already keeps
    // every portal positioned, so we don't need style mutations for that.
    mutationObserver.observe(contentDocument.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class'],
    });

    const resizeObserver = new ResizeObserver(() => bump());
    resizeObserver.observe(contentDocument.documentElement);

    return () => {
      mutationObserver.disconnect();
      resizeObserver.disconnect();
      if (timeoutId != null) clearTimeout(timeoutId);
    };
  }, [contentDocument]);

  return revision;
}

/**
 * Block selection via a single capture-phase click listener on the iframe
 * document, resolving the clicked block by geometric hit-test against the
 * targets' current rects.
 *
 * This replaces the previous per-block "click catcher" divs (position: fixed,
 * pointer-events: auto, z-index 3111111, portaled at body). Those won the
 * hit-test over any in-page stacking context, but that's exactly the problem:
 * they stole the pointer from the page, so crossing one fired spurious
 * mouseleave/mouseenter into the page and froze/flapped hover-driven UIs
 * (e.g. HubSpot mega menus that only close while their flyout matches
 * `:hover`). A document-level capture listener intercepts the click before
 * any page handler regardless of what element is visually on top, without
 * ever touching the hover event stream.
 */
/**
 * A resolved block on the page. `element` is what the block's selectors match
 * (where variants apply, what gets frozen); `anchor` is the element whose box
 * represents the block visually — the ring, badges and click hit-test use it.
 * They differ for a translated logo track (marquee / Swiper): the track's own
 * box is carried off-screen by its transform while its clipping parent is the
 * strip the user sees, so the parent is the anchor.
 */
type BlockTarget = {
  block: EmbeddedBlock;
  element: Element;
  anchor: Element;
  /** The anchor is inline-level: the ring follows its bounding box (see isInlineLevel). */
  inline: boolean;
};

/**
 * The block under (clientX, clientY), in iframe coordinates. Smallest visible
 * block whose rect contains the point wins — "innermost" semantics when
 * blocks nest or overlap. Hidden targets (e.g. inside a closed flyout) must
 * not swallow input meant for the content rendered where their box sits.
 */
function hitTestBlock(targets: BlockTarget[], clientX: number, clientY: number): string | null {
  let hit: { id: string; area: number } | null = null;
  for (const { block, anchor } of targets) {
    if (!block.id) continue;
    if (
      typeof anchor.checkVisibility === 'function' &&
      !anchor.checkVisibility({ checkOpacity: true, checkVisibilityCSS: true })
    ) {
      continue;
    }
    const r = anchor.getBoundingClientRect();
    if (clientX < r.left || clientX > r.right || clientY < r.top || clientY > r.bottom) {
      continue;
    }
    const area = r.width * r.height;
    if (!hit || area < hit.area) hit = { id: block.id, area };
  }
  return hit?.id ?? null;
}

/**
 * Which block the pointer is over, by the same geometric hit-test as clicks
 * (a document-level listener never touches the page's own hover stream).
 * Drives the small hover badge. Hit-testing is coalesced to one pass per
 * frame, and the state only changes when the hovered block does.
 */
function useHoveredBlock(contentDocument: Document | null, targets: BlockTarget[]): string | null {
  const [hoveredBlockId, setHoveredBlockId] = useState<string | null>(null);

  useEffect(() => {
    if (!contentDocument) return;
    const win = contentDocument.defaultView;
    if (!win) return;

    let frame = 0;
    let pending: { x: number; y: number } | null = null;

    const flush = () => {
      frame = 0;
      if (!pending) return;
      const { x, y } = pending;
      pending = null;
      setHoveredBlockId(hitTestBlock(targets, x, y));
    };
    const handleMove = (e: PointerEvent) => {
      pending = { x: e.clientX, y: e.clientY };
      if (!frame) frame = win.requestAnimationFrame(flush);
    };
    const handleLeave = () => {
      pending = null;
      setHoveredBlockId(null);
    };

    contentDocument.addEventListener('pointermove', handleMove, { capture: true, passive: true });
    contentDocument.documentElement.addEventListener('pointerleave', handleLeave);
    return () => {
      contentDocument.removeEventListener('pointermove', handleMove, true);
      contentDocument.documentElement.removeEventListener('pointerleave', handleLeave);
      if (frame) win.cancelAnimationFrame(frame);
      setHoveredBlockId(null);
    };
  }, [contentDocument, targets]);

  return hoveredBlockId;
}

function useBlockClickCapture(
  contentDocument: Document | null,
  targets: BlockTarget[],
  selectedBlockId: string | null,
  onBlockSelect: (blockId: string | null) => void,
  /**
   * Text mode (inline edition) owns clicks: this handler's preventDefault
   * would kill caret placement, and selection toggling makes no sense there —
   * so it unbinds entirely rather than relying on listener order.
   */
  disabled = false,
) {
  useEffect(() => {
    if (!contentDocument || disabled) return;

    const handleClick = (e: MouseEvent) => {
      const target = e.target as Element | null;
      // Overlay UI (badges, extra block input) handles its own events.
      if (target?.closest?.('[data-tinct-overlay]')) return;

      const hit = hitTestBlock(targets, e.clientX, e.clientY);
      if (hit) {
        e.preventDefault();
        e.stopPropagation();
        onBlockSelect(hit === selectedBlockId ? null : hit);
      } else {
        // Click on empty space (no block, no overlay UI) → deselect. The
        // page keeps the event untouched, same as before.
        onBlockSelect(null);
      }
    };

    contentDocument.addEventListener('click', handleClick, true);
    return () => contentDocument.removeEventListener('click', handleClick, true);
  }, [contentDocument, targets, selectedBlockId, onBlockSelect, disabled]);
}

/**
 * Portals the block badge into the iframe body, anchored 26px above the
 * target's top-left corner. Escapes any `overflow: hidden` ancestors and
 * sibling stacking contexts. Position synced via Floating UI's `autoUpdate`.
 * With `onBlockUpdate` the badge is an inline editor and takes pointer input.
 */
type InlineRing = { anchor: Element; enabled: boolean; isActive: boolean };

const INLINE_RING_ATTR = 'data-tinct-inline-ring';
const INLINE_RING_ACTIVE_ATTR = 'data-tinct-inline-ring-active';
const INLINE_RING_STYLE_ID = 'tinct-inline-ring-style';

/**
 * The ring of an inline-level target is drawn by the element itself, with
 * the block ring's exact properties (BlockOverlay's frame: inset box-shadow
 * of 1px, 3px when selected, 8px radius, colour by personalization state,
 * the same transition). Being painted by the element, it scrolls and
 * reflows with the page: the previous fixed-position ring layer was
 * repositioned one frame behind the page and lagged visibly while
 * scrolling. Inset shadow and radius add no layout — a border would widen
 * the inline box and re-wrap the line — and `box-decoration-break: clone`
 * gives every line fragment its own complete, rounded ring. The rule lives
 * in the iframe's <head> (outside the body the DOM-revision observer
 * watches) and yields to the inline text editor's own outline.
 *
 * Trade-off, accepted: painted by the element, the ring sits in the
 * element's own stacking order — a sibling stacked over the text with a
 * positive z-index covers it, where the old top-layer ring stayed above.
 * A ring glued to its text while scrolling matters more than that edge.
 */
function ensureInlineRingStyle(doc: Document) {
  if (doc.getElementById(INLINE_RING_STYLE_ID)) return;
  const style = doc.createElement('style');
  style.id = INLINE_RING_STYLE_ID;
  const ring = (width: number, color: string) => `box-shadow:inset 0 0 0 ${width}px ${color} !important;`;
  style.textContent =
    `[${INLINE_RING_ATTR}]:not([data-tinct-editing]){${ring(1, '#a3a3a3')}border-radius:8px !important;` +
    `-webkit-box-decoration-break:clone !important;box-decoration-break:clone !important;transition:box-shadow .15s ease !important;}` +
    `[${INLINE_RING_ATTR}="on"]:not([data-tinct-editing]){${ring(1, '#6366f1')}}` +
    `[${INLINE_RING_ACTIVE_ATTR}][${INLINE_RING_ATTR}="off"]:not([data-tinct-editing]){${ring(3, '#a3a3a3')}}` +
    `[${INLINE_RING_ACTIVE_ATTR}][${INLINE_RING_ATTR}="on"]:not([data-tinct-editing]){${ring(3, '#6366f1')}}`;
  (doc.head ?? doc.documentElement).appendChild(style);
}

/**
 * Writes the inline ring attributes of the current rings and clears them
 * from the elements that were rings before and no longer are. Returns the
 * current set (the next call's `previous`). Every write is guarded so an
 * unchanged ring costs no DOM mutation.
 */
export function syncInlineRingAttributes(rings: InlineRing[], previous: Set<Element>): Set<Element> {
  const current = new Set<Element>();
  for (const { anchor, enabled, isActive } of rings) {
    current.add(anchor);
    const value = enabled ? 'on' : 'off';
    if (anchor.getAttribute(INLINE_RING_ATTR) !== value) anchor.setAttribute(INLINE_RING_ATTR, value);
    if (isActive) {
      if (!anchor.hasAttribute(INLINE_RING_ACTIVE_ATTR)) anchor.setAttribute(INLINE_RING_ACTIVE_ATTR, '');
    } else if (anchor.hasAttribute(INLINE_RING_ACTIVE_ATTR)) {
      anchor.removeAttribute(INLINE_RING_ACTIVE_ATTR);
    }
  }
  for (const element of previous) {
    if (current.has(element)) continue;
    element.removeAttribute(INLINE_RING_ATTR);
    element.removeAttribute(INLINE_RING_ACTIVE_ATTR);
  }
  return current;
}

/**
 * How much the iframe is scaled on screen (the studio canvas scales the whole
 * artboard layer with a CSS transform). 1 when the frame is not scaled or
 * not measurable yet.
 */
function getFrameScale(frame: Element | null): number {
  if (!frame || !frame.clientWidth) return 1;
  const scale = frame.getBoundingClientRect().width / frame.clientWidth;
  return Number.isFinite(scale) && scale > 0 ? scale : 1;
}

/**
 * The target's effective visibility/opacity, so a badge fades and hides with
 * it. `visibility` is inherited, so the computed value already reflects
 * ancestors; `opacity` is NOT inherited but visually multiplies down the
 * tree, so walk up and multiply.
 */
function applyTargetVisibility(node: HTMLElement, target: Element, win: Window) {
  node.style.visibility = win.getComputedStyle(target).visibility;
  let opacity = 1;
  let current: Element | null = target;
  while (current) {
    const o = parseFloat(win.getComputedStyle(current).opacity);
    if (!isNaN(o)) opacity *= o;
    current = current.parentElement;
  }
  node.style.opacity = String(opacity);
}

type BadgesPortalProps = {
  target: Element;
  doc: Document;
  block: EmbeddedBlock;
  onBlockUpdate?: (block: EmbeddedBlock) => void;
};

/** Badges portaled at the iframe body, above the target (plain editor). */
function IframeBadgesPortal({ target, doc, block, onBlockUpdate }: BadgesPortalProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  const interactive = onBlockUpdate !== undefined;

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    const win = doc.defaultView;

    const update = () => {
      const rect = target.getBoundingClientRect();
      node.style.transform = `translate(${rect.left}px, ${rect.top - 26}px)`;
      if (win) applyTargetVisibility(node, target, win);
    };

    update();
    return autoUpdate(target as ReferenceElement, node, update, {
      animationFrame: true,
    });
  }, [target, doc]);

  if (!doc.body) return null;

  return (
    <OwnedOverlayPortal host={doc.body} containerStyle={DISPLAY_CONTENTS} interactive={interactive}>
      <div
        ref={ref}
        data-tinct-overlay=""
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          // Editable badges hold switches and an input, so they must win the
          // hit-test over anything the page stacks above the target.
          zIndex: interactive ? 3111112 : 99999,
          pointerEvents: interactive ? 'auto' : 'none',
          willChange: 'transform',
        }}
      >
        <BlockBadges block={block} onBlockUpdate={onBlockUpdate} />
      </div>
    </OwnedOverlayPortal>
  );
}

/**
 * Badges portaled into the canvas viewport (see OverlayHostContext). The
 * target's rect is mapped out of the iframe's scaled space into host
 * coordinates every frame, so the badge keeps its on-screen size across
 * zoom, follows pans, and is never cropped by the artboard's edges.
 *
 * Its own frame loop rather than Floating UI's: autoUpdate only re-runs when
 * the target's rect changes, and a canvas pan/zoom leaves every rect in
 * iframe coordinates untouched.
 */
function CanvasBadgesPortal({
  host,
  target,
  doc,
  block,
  onBlockUpdate,
}: BadgesPortalProps & { host: HTMLElement }) {
  const ref = useRef<HTMLDivElement | null>(null);
  const interactive = onBlockUpdate !== undefined;

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    const win = doc.defaultView;
    const frame = win?.frameElement ?? null;
    let last = '';
    let raf = 0;

    const tick = () => {
      raf = requestAnimationFrame(tick);
      if (!frame) return;
      const rect = target.getBoundingClientRect();
      const frameRect = frame.getBoundingClientRect();
      const hostRect = host.getBoundingClientRect();
      const scale = getFrameScale(frame);
      const x = frameRect.left - hostRect.left + rect.left * scale;
      const y = frameRect.top - hostRect.top + rect.top * scale;
      const next = `translate(${x}px, ${y - 26}px)`;
      if (next !== last) {
        last = next;
        node.style.transform = next;
      }
      if (win) applyTargetVisibility(node, target, win);
    };
    tick();

    return () => cancelAnimationFrame(raf);
  }, [host, target, doc]);

  return createPortal(
    <div
      ref={ref}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        // Above the artboard layer, below the canvas's own controls (z-10).
        zIndex: 5,
        pointerEvents: interactive ? 'auto' : 'none',
        cursor: 'default',
        willChange: 'transform',
      }}
    >
      <BlockBadges block={block} onBlockUpdate={onBlockUpdate} />
    </div>,
    host,
  );
}

function BadgesPortal(props: BadgesPortalProps) {
  const host = useOverlayHost();
  return host ? <CanvasBadgesPortal host={host} {...props} /> : <IframeBadgesPortal {...props} />;
}

// -- Hook --

export type UseIframeBlockOverlaysOptions = {
  contentDocument: Document | null;
  blocks: EmbeddedBlock[];
  selectedBlockId: string | null;
  onBlockSelect: (blockId: string | null) => void;
  /** Makes the selected block's badge an inline editor. */
  onBlockUpdate?: (block: EmbeddedBlock) => void;
  blockVariants?: EmbeddedBlockVariant[];
  showAllLabels?: boolean;
  /**
   * `text` = inline text edition owns clicks (useInlineTextEditor); the
   * select-mode click capture unbinds so caret placement works. Overlays and
   * badges keep rendering — they still show where blocks are.
   */
  interactionMode?: 'select' | 'text';
};

export function useIframeBlockOverlays({
                                         contentDocument,
                                         blocks,
                                         selectedBlockId,
                                         onBlockSelect,
                                         onBlockUpdate,
                                         showAllLabels = false,
                                         interactionMode = 'select',
                                       }: UseIframeBlockOverlaysOptions): ReactNode {
  const domRevision = useIframeDomRevision(contentDocument);

  const targets = useMemo(() => {
    if (!contentDocument) return [];

    return blocks
      .filter((block) => block.id)
      .map((block) => {
        const element = findBlockElement(block, contentDocument);
        if (!element) return null;

        // Logo carousels auto-slide; pin the transform so the overlay stays
        // put. A track pinned mid-slide keeps its logos in view but its own
        // box is off-screen — its parent (the clipping wrapper) is the strip.
        let anchor: Element = element;
        if (block.type === 'customer_logo') {
          const { translated } = freezeTransform(element as HTMLElement, contentDocument);
          if (translated && element.parentElement) anchor = element.parentElement;
        }

        // Inline-level anchors keep their layout untouched; their ring follows
        // their box instead of living inside it.
        const inline = isInlineLevel(anchor);
        if (!inline) ensurePositioned(anchor as HTMLElement);

        // Selection affordance the click catchers used to provide. Guarded so
        // we only write once (see ensurePositioned on why that matters).
        if (block.id && !(anchor as HTMLElement).style.cursor) {
          (anchor as HTMLElement).style.cursor = 'pointer';
        }

        return { block, element, anchor, inline };
      })
      .filter(Boolean) as BlockTarget[];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentDocument, blocks, domRevision]);

  // Release frozen tracks that stopped being targets (artboard deactivated,
  // block gone) so inactive artboards' marquees resume. Elements still
  // targeted keep their pin — unfreezing/refreezing them each revision would
  // make the track jump.
  const previousTargetsRef = useRef<Element[]>([]);
  useEffect(() => {
    const current = new Set(targets.map((t) => t.element));
    for (const element of previousTargetsRef.current) {
      if (!current.has(element)) unfreezeTransform(element as HTMLElement);
    }
    previousTargetsRef.current = targets.map((t) => t.element);
  }, [targets]);
  useEffect(
    () => () => {
      previousTargetsRef.current.forEach((element) => unfreezeTransform(element as HTMLElement));
    },
    [],
  );

  useBlockClickCapture(
    contentDocument,
    targets,
    selectedBlockId,
    onBlockSelect,
    interactionMode === 'text',
  );

  const hoveredBlockId = useHoveredBlock(contentDocument, targets);

  // Inline-level targets: an outline on the element (see ensureInlineRingStyle).
  const inlineRings = useMemo<InlineRing[]>(
    () =>
      targets
        .filter((t) => t.inline && t.block.id)
        .map((t) => ({
          anchor: t.anchor,
          enabled: isBlockPersonalizationEnabled(t.block),
          isActive: t.block.id === selectedBlockId,
        })),
    [targets, selectedBlockId]
  );
  const inlineRingElementsRef = useRef(new Set<Element>());
  useEffect(() => {
    if (!contentDocument) return;
    ensureInlineRingStyle(contentDocument);
    inlineRingElementsRef.current = syncInlineRingAttributes(inlineRings, inlineRingElementsRef.current);
  }, [contentDocument, inlineRings]);
  useEffect(
    () => () => {
      inlineRingElementsRef.current = syncInlineRingAttributes([], inlineRingElementsRef.current);
    },
    [],
  );

  return (
    <>
      {targets.map(({ block, anchor, inline }) => {
    const isSelected = block.id === selectedBlockId;
    const isHovered = block.id === hoveredBlockId;

    return (
      <Fragment key={block.id ?? undefined}>
        {!inline && (
          /* `display: contents`: the owned container generates no box, so the
             overlay's `absolute inset: 0` keeps resolving against `anchor`
             (made `position: relative` by ensurePositioned). */
          <OwnedOverlayPortal host={anchor} containerStyle={DISPLAY_CONTENTS}>
            <BlockOverlay block={block} isActive={isSelected} />
          </OwnedOverlayPortal>
        )}
        {/* Two badges: the selected block gets the full, editable one; a
            hovered (or, with showAllLabels, any) block the small read-only
            one — BlockBadges collapses to icon + label without onBlockUpdate. */}
        {contentDocument && (isSelected || isHovered || showAllLabels) && (
          <BadgesPortal
            target={anchor}
            doc={contentDocument}
            block={block}
            onBlockUpdate={isSelected ? onBlockUpdate : undefined}
          />
        )}
      </Fragment>
    );
      })}
    </>
  );
}
